import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Drawer,
  Form,
  SelectPicker,
  Button,
  Stack,
  IconButton,
  Message,
  useToaster,
  List,
  InputNumber,
  Accordion
} from 'rsuite';
import PlusIcon from '@rsuite/icons/Plus';
import MinusIcon from '@rsuite/icons/Minus';
import { RiUserLocationLine } from 'react-icons/ri';
import { useAppDispatch, useAppSelector } from '@/config/hooks';
import { getZonePrioritiesWithUsers, updateUserAccessPriority } from '@/services/access.service';
import { getFloors } from '@/services/floor.service';
import { getZones } from '@/services/zone.service';

const slimText = {
  fontSize: '0.9em',
  color: '#97969B',
  fontWeight: 'lighter',
  paddingBottom: 5
};

const titleStyle = {
  fontSize: '1.1em',
  fontWeight: 600,
  color: '#333',
  margin: 0
};

const subTitleStyle = {
  fontSize: '1em',
  fontWeight: 500,
  color: '#666',
  marginLeft: 30
};

const daysOfWeek = [
  { label: 'Monday', value: 1 },
  { label: 'Tuesday', value: 2 },
  { label: 'Wednesday', value: 3 },
  { label: 'Thursday', value: 4 },
  { label: 'Friday', value: 5 },
  { label: 'Saturday', value: 6 },
  { label: 'Sunday', value: 7 }
];

const AddPrio = ({ open, onClose, users, withPriority }) => {
  const dispatch = useAppDispatch();
  const toaster = useToaster();
  const [loading, setLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [zones, setZones] = useState([]);

  // Move this hook to the component top level
  const zonePrioritiesWithUsers = useAppSelector(state => state.access.zonePrioritiesWithUsers);

  const floors = useAppSelector((state: any) => state.floor?.data || []);
  const allZones = useAppSelector((state: any) => state.zone?.data);

  // Get zones and sort them by floor order
  useEffect(() => {
    if (allZones?.length > 0 && floors.length > 0) {
      const mappedZones = allZones.map(item => {
        const floorObj = floors.find(floor => floor._id === item.floor);
        return {
          ...item,
          value: item._id,
          label: `${floorObj?.building?.address || 'building'} / ${
            floorObj?.name || 'Unknown Floor'
          } / ${item.name}`,
          floor: item.floor,
          sortOrder: floorObj?.sortOrder || 0
        };
      });

      setZones(mappedZones.sort((a, b) => a.sortOrder - b.sortOrder));
    }
  }, [allZones, floors]);

  useEffect(() => {
    dispatch(getFloors());
    dispatch(getZones());
  }, [dispatch]);

  const [formValue, setFormValue] = useState({
    User: ''
  });

  const [localAccessList, setLocalAccessList] = useState([
    {
      zone: null,
      data: [
        {
          dayOfWeek: null,
          priority: null
        }
      ]
    }
  ]);

  const handleSubmit = async () => {
    if (!formValue.User) {
      toaster.push(<Message type="error">Please select a user</Message>, {
        placement: 'topCenter'
      });
      return;
    }

    // Check if any zone is not selected
    if (localAccessList.some(item => !item.zone)) {
      toaster.push(<Message type="error">Please select a zone for each access row</Message>, {
        placement: 'topCenter'
      });
      return;
    }

    // Check if any day or priority is missing
    const invalidPriorities = localAccessList.some(item =>
      item.data.some(d => !d.dayOfWeek || d.priority === null || d.priority === undefined)
    );
    if (invalidPriorities) {
      toaster.push(<Message type="error">Please fill in all day and priority values</Message>, {
        placement: 'topCenter'
      });
      return;
    }

    try {
      setIsSaving(true);

      // Format the data for the API call
      const priorityConfigs = localAccessList.flatMap(({ zone, data }) =>
        data.map(({ dayOfWeek, priority }) => ({
          zone,
          dayOfWeek: parseInt(dayOfWeek),
          priority: parseInt(priority)
        }))
      );

      // Get existing priorities for this user from the Redux store
      const existingPriorities =
        zonePrioritiesWithUsers.filter(item => item.user && item.user._id === formValue.User) || [];

      // Map existing priorities to the same format as new ones
      const existingPriorityConfigs = existingPriorities.map(item => ({
        zone: item.zone._id || item.zone,
        dayOfWeek: parseInt(item.dayOfWeek),
        priority: parseInt(item.priority),
        _id: item._id // Keep the ID for existing entries
      }));

      // Create a map of existing entries by zone and day to avoid duplicates
      const existingMap = {};
      existingPriorityConfigs.forEach(item => {
        const key = `${item.zone}-${item.dayOfWeek}`;
        existingMap[key] = item;
      });

      // Add new entries, overwriting existing ones with the same zone and day
      priorityConfigs.forEach(item => {
        const key = `${item.zone}-${item.dayOfWeek}`;
        existingMap[key] = item;
      });

      // Convert map back to array
      const mergedPriorityConfigs = Object.values(existingMap);

      // Send the complete list to the API
      const response = await dispatch(
        updateUserAccessPriority(formValue.User, mergedPriorityConfigs)
      );

      if (response?.status === 200) {
        toaster.push(
          <Message type="success">
            {t('Individual access with priority added successfully')}
          </Message>,
          { placement: 'topCenter' }
        );

        // Fetch updated zone priorities
        dispatch(getZonePrioritiesWithUsers());

        setFormValue({ User: '' });
        setLocalAccessList([
          {
            zone: null,
            data: [{ dayOfWeek: null, priority: null }]
          }
        ]);

        onClose();
      }
    } catch (error) {
      console.error('Error adding individual access with priority:', error);
      toaster.push(
        <Message type="error">
          {t(error?.response?.data?.message) ||
            t(error?.message) ||
            t('Failed to add access with priority')}
        </Message>,
        { placement: 'topCenter' }
      );
    } finally {
      setIsSaving(false);
    }
  };

  const handleAddZoneRow = () => {
    setLocalAccessList([
      ...localAccessList,
      {
        zone: null,
        data: [
          {
            dayOfWeek: null,
            priority: null
          }
        ]
      }
    ]);
  };

  const handleAddRow = indexRow => {
    setLocalAccessList(prevList =>
      prevList.map((row, rowIndex) => {
        if (rowIndex !== indexRow) return row;
        return {
          ...row,
          data: [
            ...row.data,
            {
              dayOfWeek: null,
              priority: null
            }
          ]
        };
      })
    );
  };

  // Modified handleChange to auto-populate days when zone is selected
  const handleChange = (indexRow, key, value, index = null) => {
    setLocalAccessList(prevList =>
      prevList.map((row, rowIndex) => {
        if (rowIndex !== indexRow) return row;

        // If we're changing the zone, auto-populate all days of the week with priority 1
        if (key === 'zone') {
          return {
            ...row,
            zone: value,
            // Replace data with all days of the week
            data: daysOfWeek.map(day => ({
              dayOfWeek: day.value,
              priority: 1 // Default priority
            }))
          };
        }

        // For other changes
        if (index === null) {
          return { ...row, [key]: value };
        } else {
          const updatedData = row.data.map((item, itemIndex) =>
            itemIndex === index ? { ...item, [key]: value } : item
          );
          return { ...row, data: updatedData };
        }
      })
    );
  };

  const handleDeleteZoneRow = indexRow => {
    setLocalAccessList(prevList => prevList.filter((_, rowIndex) => rowIndex !== indexRow));
  };

  const handleRemoveRow = (indexRow, index) => {
    // Prevent removing the last row
    if (localAccessList[indexRow].data.length === 1) {
      return;
    }

    setLocalAccessList(prevList =>
      prevList.map((row, rowIndex) => {
        if (rowIndex !== indexRow) return row;
        const updatedData = row.data.filter((_, itemIndex) => itemIndex !== index);
        return { ...row, data: updatedData };
      })
    );
  };

  // Don't render if withPriority is false
  if (!withPriority) {
    return null;
  }
  const { t } = useTranslation();
  return (
    <Drawer backdrop="static" size="md" placement="right" onClose={onClose} open={open}>
      <Drawer.Header>
        <Drawer.Title>{t('Add Individual Access with Priority')}</Drawer.Title>
        <Drawer.Actions>
          <Button
            onClick={handleSubmit}
            appearance="primary"
            loading={isSaving}
            disabled={isSaving}
          >
            {t('Save')}
          </Button>
          <Button onClick={onClose} appearance="subtle" disabled={isSaving}>
            {t('Cancel')}
          </Button>
        </Drawer.Actions>
      </Drawer.Header>

      <Drawer.Body>
        <Form fluid formValue={formValue} onChange={setFormValue}>
          <Form.Group>
            <Form.ControlLabel>{t('User *')}</Form.ControlLabel>
            <SelectPicker
              block
              data={users.map(user => ({
                label: `${user.firstname} ${user.lastname}`,
                value: user._id
              }))}
              value={formValue.User}
              onChange={value => setFormValue(prev => ({ ...prev, User: value }))}
              searchable={true}
              cleanable={false}
              placeholder={t('Select User')}
            />
          </Form.Group>

          <Stack
            style={{ marginTop: 50, marginBottom: 20, marginLeft: 10 }}
            spacing={10}
            alignItems="center"
          >
            <RiUserLocationLine size={20} />
            <h6 style={titleStyle}>{t('Manage accesses with priority')}</h6>
          </Stack>

          <div className="ml-10">
            <Accordion defaultActiveKey={0}>
              {localAccessList.map((row, indexRow) => (
                <Accordion.Panel
                  key={indexRow}
                  header={
                    <Stack spacing={10} alignItems="flex-start">
                      {row.zone ? (
                        <span style={subTitleStyle}>
                          {zones.find(z => z.value === row.zone)?.label || t('No zone selected')}
                        </span>
                      ) : (
                        <span style={subTitleStyle}>{t('New Zone Access')}</span>
                      )}
                    </Stack>
                  }
                  defaultExpanded
                >
                  <Stack justifyContent="space-between">
                    <div style={{ textAlign: 'left', marginLeft: '28px' }}>
                      <div style={slimText}>Zone *</div>
                      <SelectPicker
                        data={zones}
                        value={row.zone}
                        onChange={value => handleChange(indexRow, 'zone', value)}
                        style={{ width: 250 }}
                        placement="auto"
                        disabledItemValues={localAccessList
                          .filter((_, i) => i !== indexRow)
                          .map(item => item.zone)
                          .filter(Boolean)}
                        cleanable={false}
                        placeholder={t('Select Zone')}
                      />
                    </div>
                    <IconButton
                      appearance="subtle"
                      size="sm"
                      icon={<MinusIcon color="red" />}
                      onClick={() => handleDeleteZoneRow(indexRow)}
                      disabled={localAccessList.length === 1}
                    />
                  </Stack>

                  <List style={{ marginLeft: 50, marginTop: 20 }}>
                    {row.data.map((item, index) => (
                      <List.Item key={index}>
                        <Stack spacing={20} wrap>
                          <div style={{ textAlign: 'left' }}>
                            <div style={slimText}>{t('Day of week *')}</div>
                            <SelectPicker
                              data={daysOfWeek}
                              value={item.dayOfWeek}
                              onChange={value => handleChange(indexRow, 'dayOfWeek', value, index)}
                              style={{ width: 250 }}
                              placement="auto"
                              disabledItemValues={row.data
                                .filter((_, i) => i !== index)
                                .map(item => item.dayOfWeek)
                                .filter(Boolean)}
                              placeholder={t('Select Day')}
                            />
                          </div>

                          <div style={{ textAlign: 'left' }}>
                            <div style={slimText}>{t('Priority *')}</div>
                            <InputNumber
                              value={item.priority}
                              onChange={value => handleChange(indexRow, 'priority', value, index)}
                              style={{ width: 100 }}
                              min={1}
                            />
                          </div>

                          <Stack spacing={5} alignItems="flex-end">
                            <IconButton
                              onClick={() => handleRemoveRow(indexRow, index)}
                              icon={<MinusIcon />}
                              size="xs"
                              disabled={row.data.length === 1}
                            />
                            {index === row.data.length - 1 && (
                              <IconButton
                                onClick={() => handleAddRow(indexRow)}
                                icon={<PlusIcon />}
                                size="xs"
                              />
                            )}
                          </Stack>
                        </Stack>
                      </List.Item>
                    ))}
                  </List>
                </Accordion.Panel>
              ))}
            </Accordion>

            <IconButton
              onClick={handleAddZoneRow}
              icon={<PlusIcon color="var(--rs-primary-800)" />}
              style={{ marginTop: 10 }}
            />
          </div>
        </Form>
      </Drawer.Body>
    </Drawer>
  );
};

export default AddPrio;
